import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, X, Sparkles, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "./shared";
import { resolveUrl, waLink } from "@/lib/api";

export const ICON_OPTIONS = ["Trophy", "BookOpenCheck", "Building2", "GraduationCap", "Users", "Target", "Rocket", "Award", "Brain", "Cpu", "Lightbulb", "Medal"];

export const Services = ({ services = [], whatsappNumber = "" }) => {
  const [selectedService, setSelectedService] = useState(null);

  const handleConsult = (serviceTitle) => {
    const text = `Halo Binara Labs, saya tertarik dengan program layanan "${serviceTitle}" dan ingin berdiskusi lebih lanjut.`;
    const targetNumber = whatsappNumber || "6285000088990"; // Fallback nomor WhatsApp default
    window.open(waLink(targetNumber, text), "_blank");
    setSelectedService(null);
  };

  return (
    <section id="services" data-testid="services-section" className="relative py-24 md:py-32 bg-navy-soft border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <Eyebrow testid="services-eyebrow">Layanan Kami</Eyebrow>
          <h2 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tight max-w-3xl leading-tight">
            Solusi lengkap untuk <span className="text-cyan">siswa</span> dan <span className="text-gradient-gold">korporat</span>.
          </h2>
        </Reveal>

        {/* Tampilan Ringkas di Halaman Utama */}
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = Icons[s.icon] || Sparkles;
            return (
              <motion.div
                key={s.id || i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelectedService(s)}
                data-testid={`service-card-${i}`}
                className="group relative rounded-3xl border border-white/10 bg-navy-card/50 p-8 overflow-hidden hover:border-cyan/40 hover:-translate-y-2 cursor-pointer transition-all duration-300 flex flex-col justify-between"
              >
                <div className="absolute -top-24 -right-24 w-48 h-48 radial-glow opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="relative z-10">
                  <div className="flex items-center justify-between">
                    <div className="grid place-items-center w-14 h-14 rounded-2xl bg-gradient-to-br from-cyan/20 to-gold/10 border border-white/10 text-cyan group-hover:scale-110 transition-transform duration-300">
                      <Icon className="w-7 h-7" strokeWidth={1.6} />
                    </div>
                    <span className="font-heading text-5xl font-black text-white/10 leading-none">0{i + 1}</span>
                  </div>
                  <h3 className="mt-8 font-heading font-semibold text-2xl leading-snug text-white group-hover:text-cyan transition-colors">{s.title}</h3>
                </div>
                <div className="mt-8 pt-4 border-t border-white/10 flex items-center justify-between text-sm font-semibold text-slate-400 group-hover:text-white transition-colors">
                  <span>Klik untuk lihat detail</span>
                  <ArrowUpRight className="w-4 h-4 text-cyan group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* POPUP / MODAL DETAIL LAYANAN */}
      <AnimatePresence>
        {selectedService && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedService(null)}
            className="fixed inset-0 z-[100] bg-navy/90 backdrop-blur-md grid place-items-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl rounded-3xl border border-white/15 bg-navy-card p-7 md:p-9 shadow-2xl overflow-hidden my-8"
            >
              <button
                onClick={() => setSelectedService(null)}
                className="absolute top-4 right-4 grid place-items-center w-10 h-10 rounded-full border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition"
              >
                <X className="w-5 h-5" />
              </button>

              {(() => {
                const PopupIcon = Icons[selectedService.icon] || Sparkles;
                return (
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <span className="grid place-items-center w-14 h-14 rounded-2xl bg-cyan/15 border border-cyan/30 text-cyan">
                        <PopupIcon className="w-7 h-7" />
                      </span>
                      <div>
                        <span className="text-xs uppercase tracking-widest text-gold font-semibold">Detail Layanan</span>
                        <h3 className="font-heading font-bold text-2xl md:text-3xl text-white mt-0.5">{selectedService.title}</h3>
                      </div>
                    </div>

                    <p className="text-sm md:text-base text-slate-300 leading-relaxed">{selectedService.desc}</p>

                    {selectedService.points && selectedService.points.length > 0 && (
                      <div className="mt-6 pt-6 border-t border-white/10">
                        <h4 className="text-xs uppercase tracking-widest text-slate-400 font-semibold mb-3">Poin Penting & Benefit:</h4>
                        <ul className="grid sm:grid-cols-2 gap-3">
                          {selectedService.points.map((p, k) => (
                            <li key={k} className="flex items-center gap-2.5 text-sm text-slate-200 bg-white/5 border border-white/10 rounded-xl px-4 py-3">
                              <span className="w-2 h-2 rounded-full bg-gold shrink-0" /> {p}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="mt-8 pt-6 border-t border-white/10 flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleConsult(selectedService.title)}
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] px-6 py-3.5 font-body font-bold text-white hover:shadow-[0_0_30px_rgba(37,211,102,0.4)] transition-all duration-300"
                      >
                        <MessageCircle className="w-5 h-5" /> Konsultasi via WhatsApp
                      </button>
                    </div>
                  </div>
                );
              })()}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};